<template>
  <div>
    <ItemRow :class="$style.row">
      <ItemWithLabel
        :class="[$style.col1Name, $style.columnWrap]"
        :column="columns[0]"
        data-test-id="CustomCommandTemplateRow__Name"
        :title="itemData.name"
      >
        <span>{{ itemData.name }}</span>
      </ItemWithLabel>
      <ItemWithLabel
        :class="$style.col2Schedule"
        :column="columns[1]"
        data-test-id="CustomCommandTemplateRow__LaunchType"
      >
        <span>{{ itemData.launchType }}</span>
      </ItemWithLabel>
      <ItemWithLabel
        :class="[$style.col3Command, $style.columnWrap]"
        :column="columns[2]"
        data-test-id="CustomCommandTemplateRow__Command"
        :title="itemData.command"
      >
        {{ itemData.command }}
      </ItemWithLabel>
      <ItemWithLabel
        :class="$style.col4Os"
        :column="columns[3]"
        data-test-id="CustomCommandTemplateRow__Os"
      >
        <Icon
          data-test-id="CustomCommandTemplateRow__OsIcon"
          :icon="systemOsIcon"
          inline
          :scale="1.5"
          :text="systemOsLabel"
        />
        <span
          :class="$style.osLabel"
        > {{ systemOsLabel }}
        </span>
      </ItemWithLabel>
      <ItemWithLabel
        :class="$style.col5"
        :column="columns[4]"
      >
        <ButtonSecondary
          :class="$style.space"
          data-test-id="CustomCommandTemplateRow__EditButton"
          :outline="false"
          text="Edit"
          @click="handleEditTemplate"
        />
        <ButtonSecondary
          v-if="showDeleteBtn"
          :class="$style.space"
          data-test-id="CustomCommandTemplateRow__DeleteButton"
          :outline="false"
          text="Delete"
          type="error"
          @click="openDeleteModal"
        />
      </ItemWithLabel>
    </ItemRow>
  </div>
</template>

<script>
import {
  ButtonSecondary,
  Icon,
  ItemRow,
  ItemWithLabel,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';
import { noop } from 'lodash';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import CommandTypes from 'client/Commands/constants/CommandTypes';
import ModalEventBus from 'components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import UtilConstants, {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import pluralize from 'pluralize';

const { linux, windows, mac } = CommandTypes;
const { dataNotAvailable } = UtilConstants;
const commandTemplateCollection = StoreModuleMap[ItemTypeConsts.commandCustomTemplate];
const { commandTemplateEdit } = ItemTypeConsts;

export default {
  name: 'CommandTemplateManageRow',
  components: {
    ButtonSecondary,
    Icon,
    ItemRow,
    ItemWithLabel,
  },
  props: {
    columns: {
      type: Array,
      default: () => ([]),
    },
    itemData: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      showCancelModal: false,
      loadingEditTemplate: false,
      showModalEditCommandTemplate: false,
    };
  },
  computed: {
    ...mapGetters('SettingsModule', [
      'isAdminWithBilling',
      'isAdmin',
    ]),
    showDeleteBtn() {
      return MspState.isAnMsp && (this.isAdminWithBilling || this.isAdmin);
    },
    systemOsIcon() {
      const iconMap = {
        [linux]: 'linux',
        [windows]: 'windows',
        [mac]: 'apple',
      };
      return iconMap[this.itemData.commandType] || dataNotAvailable;
    },
    systemOsLabel() {
      const { commandType } = this.itemData;
      return commandType.charAt(0).toUpperCase() + commandType.slice(1);
    },
  },
  methods: {
    ...mapActions(commandTemplateCollection, [
      'deleteItems',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    handleConfirmCloseEditTemplate() {
      this.showModalEditCommandTemplate = false;
    },

    handleDeleteTemplate() {
      this.deleteItems([this.itemData.id]).then((result) => {
        const { deletedIds, failedTasks } = result;
        if (failedTasks.length > 0) {
          this.triggerErrorNotification({
            message: `The selected ${pluralize(`${this.itemData.name}`, failedTasks.length)} could not be deleted.`,
            title: 'Error',
          });
        } else {
          this.triggerSuccessNotification({
            message: `${pluralize('Command', deletedIds.length)} successfully deleted.`,
            title: 'Command template deleted',
          });
        }
      }).catch(() => {
        this.triggerErrorNotification({
          message: 'Error deleting command template.',
          title: 'Error',
        });
      }).finally(() => {
        ModalEventBus.close();
      });
    },

    async handleEditTemplate() {
      this.$router.push(`${ItemTypeRoutes[commandTemplateEdit]}/${this.itemData.id}/details`).catch(noop);
    },

    openDeleteModal() {
      const componentProps = {
        bodyText: 'Upon deletion of the template, commands derived from this template will be unlinked and still be available in the organizations.',
        headerText: 'Are you sure you would like to delete the command template?',
        continueText: 'yes, delete',
        handleContinue: () => {
          componentProps.loading = true;
          this.handleDeleteTemplate();
        },
        loading: false,
        mainActionAttention: true,
      };

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps,
      });
    },
  },
};
</script>

<style module scoped>

.row {
  grid-template:
    'col1 col1' auto
    'col2 col2' auto
    'col3 .' auto
    'col4 col5' auto
    'col6 .' auto /
    1fr 1fr;
  padding: 0 var(--jc-spacer-small);
}

.row:not(:last-child) {
  border-bottom: var(--list-border) !important;
}

.col1Name {
  grid-area: col1;
}

.col2Schedule {
  grid-area: col2;
}

.col3Command {
  grid-area: col3;
}

.col4Os {
  grid-area: col4;
}

.col5 {
  grid-area: col5;
}

.space {
  margin-right: var(--jc-spacer-small);
}

.columnWrap {
  display: inline-block;
  margin-bottom: var(--jc-spacer);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.osLabel {
  margin-bottom: 10px !important;
}

@media (min-width: 48rem) {
  .row {
    grid-template: 'col1 col2 col3 col4 col5' auto / var(--list-column-template);
  }
}
</style>
